import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { encodeTime } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { CICODateSelect } from '../../../general/vol-dashboard/vol-dashboard-my-elements/main-section/my-activities/CICO/CICODateSelect';

interface SessionOption {
  sessionId: string;
  activityId: string;
  activityName: string;
  sessionName: string;
  startTime: string | null;
  endTime: string | null;
  activityDate: string;
}

interface CheckInOption {
  volunteerId: string;
  sessions: SessionOption[];
}

export interface BulkAttendanceVolunteer {
  volunteerId: string;
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  volunteers: BulkAttendanceVolunteer[];
  onComplete: () => void;
}

const sortByStart = (sessions: SessionOption[]) =>
  [...sessions].sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''));

const fmtTime = (t: string | null) =>
  t
    ? DateTime.fromFormat(t, 'HH:mm:ss')
        .toFormat('h:mma')
        .toLowerCase()
    : '';

const sessionTimeRange = (s: SessionOption) => {
  const start = fmtTime(s.startTime);
  const end = fmtTime(s.endTime);
  if (start && end) return `${start} – ${end}`;
  return start;
};

const sessionLabel = (s: SessionOption) => {
  const range = sessionTimeRange(s);
  return range ? `${s.sessionName} · ${range}` : s.sessionName;
};

export function BulkCheckInDialog({ open, onClose, volunteers, onComplete }: Props) {
  const apiUrl = useApiUrl();
  const { run } = useCampfireFetch({ defer: true });
  const { setSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [options, setOptions] = useState<CheckInOption[]>([]);
  const [alreadyCheckedIn, setAlreadyCheckedIn] = useState<number>(0);
  // volunteerId -> chosen sessionId
  const [selectedSession, setSelectedSession] = useState<Record<string, string>>({});
  // volunteerId -> chosen check-in time ('' means "Now", resolved at submit)
  const [checkInTime, setCheckInTime] = useState<Record<string, Date | ''>>({});
  // time shown on the "set for all" control
  const [bulkTime, setBulkTime] = useState<Date | ''>('');

  const applyTimeToAll = (value: Date) => {
    setBulkTime(value);
    setCheckInTime(() => {
      const next: Record<string, Date | ''> = {};
      options.forEach((o) => {
        next[o.volunteerId] = value;
      });
      return next;
    });
  };

  const nameFor = useMemo(() => {
    const map: Record<string, string> = {};
    volunteers.forEach((v) => {
      map[v.volunteerId] = v.name;
    });
    return map;
  }, [volunteers]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    run({
      method: 'post',
      url: `${apiUrl}/vm/session/cico/attendance-options`,
      data: {
        volunteerIds: volunteers.map((v) => v.volunteerId),
        activityDate: DateTime.local().toFormat('yyyy-MM-dd'),
      },
    })
      .then((response) => {
        const data = response?.data?.data;
        const checkInOptions: CheckInOption[] = data?.checkInOptions ?? [];
        setOptions(checkInOptions);
        setAlreadyCheckedIn((data?.checkedIn ?? []).length);
        const defaults: Record<string, string> = {};
        checkInOptions.forEach((o) => {
          const sorted = sortByStart(o.sessions);
          if (sorted.length > 0) {
            defaults[o.volunteerId] = sorted[0].sessionId;
          }
        });
        setSelectedSession(defaults);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const eligibleCount = options.length;
  const skippedCount = volunteers.length - eligibleCount - alreadyCheckedIn;

  const handleSubmit = () => {
    const selected = options
      .map((o) => {
        const session = o.sessions.find((s) => s.sessionId === selectedSession[o.volunteerId]);
        return session ? { volunteerId: o.volunteerId, session } : null;
      })
      .filter((x): x is { volunteerId: string; session: SessionOption } => x !== null);

    if (selected.length === 0) return;

    // activityDate comes from the session each volunteer is actually being checked into,
    // grouped so a selection spanning multiple dates stays correct
    const byDate: Record<string, typeof selected> = {};
    selected.forEach((item) => {
      if (!byDate[item.session.activityDate]) {
        byDate[item.session.activityDate] = [];
      }
      byDate[item.session.activityDate].push(item);
    });

    setSubmitting(true);
    Promise.all(
      Object.entries(byDate).map(([activityDate, items]) =>
        run({
          method: 'post',
          url: `${apiUrl}/vm/session/cico/checkin`,
          data: {
            activityDate,
            checkInItems: items.map(({ volunteerId, session }) => {
              const chosen = checkInTime[volunteerId];
              return {
                sessionId: session.sessionId,
                volunteerId,
                checkIn: chosen ? encodeTime(DateTime.fromJSDate(chosen)) : encodeTime(DateTime.local()),
              };
            }),
          },
        })
      )
    )
      .then((responses) => {
        const failed = responses.find((r: any) => !r?.ok);
        if (failed) {
          setSnackbar({ open: true, message: 'Failed to check in volunteers', variant: 'error' });
        } else {
          setSnackbar({ open: true, message: `Checked in ${selected.length} volunteer(s)`, variant: 'success' });
          onComplete();
          onClose();
        }
      })
      .finally(() => setSubmitting(false));
  };

  const groupedVolunteerIds = useMemo(() => {
    // sort volunteers so those heading to the same session sit together
    return [...options].sort((a, b) => {
      const sa = a.sessions.find((s) => s.sessionId === selectedSession[a.volunteerId]);
      const sb = b.sessions.find((s) => s.sessionId === selectedSession[b.volunteerId]);
      const ka = `${sa?.activityName ?? ''}${sa?.sessionName ?? ''}`;
      const kb = `${sb?.activityName ?? ''}${sb?.sessionName ?? ''}`;
      return ka.localeCompare(kb);
    });
  }, [options, selectedSession]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography style={{ fontWeight: 600, fontSize: '16px' }}>Check In Volunteers</Typography>
        <IconButton size='small' onClick={onClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers style={{ minHeight: 200 }}>
        {loading ? (
          <Box display='flex' justifyContent='center' padding={4}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {eligibleCount > 0 && (
              <Typography variant='body2' style={{ fontWeight: 600, color: '#464646', marginBottom: '8px' }}>
                {DateTime.local().toFormat('cccc, d LLLL yyyy')}
              </Typography>
            )}
            {(skippedCount > 0 || alreadyCheckedIn > 0) && (
              <Box marginBottom='12px'>
                {alreadyCheckedIn > 0 && (
                  <Typography variant='body2' style={{ color: '#888' }}>
                    {alreadyCheckedIn} already checked in (skipped)
                  </Typography>
                )}
                {skippedCount > 0 && (
                  <Typography variant='body2' style={{ color: '#888' }}>
                    {skippedCount} not rostered today with check-in enabled (skipped)
                  </Typography>
                )}
              </Box>
            )}
            {eligibleCount > 0 && (
              <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                style={{ padding: '4px 0 12px', borderBottom: '1px solid #f0f0f0', marginBottom: '4px', gap: '12px' }}
              >
                <Typography variant='body2' style={{ fontWeight: 600, color: '#464646' }}>
                  Set check-in time for all
                </Typography>
                <CICODateSelect field='bulk-checkin-all' time={bulkTime} onChange={applyTimeToAll} />
              </Box>
            )}
            {eligibleCount === 0 ? (
              <Typography variant='body2' style={{ textAlign: 'center', color: '#999', padding: '24px' }}>
                No selected volunteers are rostered on a check-in enabled session today.
              </Typography>
            ) : (
              groupedVolunteerIds.map((o) => {
                const sorted = sortByStart(o.sessions);
                return (
                  <Box
                    key={o.volunteerId}
                    display='flex'
                    alignItems='center'
                    justifyContent='space-between'
                    style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', gap: '12px' }}
                  >
                    <Typography style={{ fontSize: '14px', fontWeight: 500, flex: 1 }}>
                      {nameFor[o.volunteerId] ?? 'Volunteer'}
                    </Typography>
                    <Box display='flex' alignItems='center' justifyContent='flex-end' style={{ gap: '8px' }}>
                      {sorted.length === 1 ? (
                        <Typography style={{ fontSize: '13px', color: '#646464', textAlign: 'right' }}>
                          {sorted[0].activityName} — {sessionLabel(sorted[0])}
                        </Typography>
                      ) : (
                        <TextField
                          select
                          size='small'
                          value={selectedSession[o.volunteerId] ?? ''}
                          onChange={(e) => setSelectedSession((prev) => ({ ...prev, [o.volunteerId]: e.target.value }))}
                          style={{ minWidth: 200 }}
                        >
                          {sorted.map((s) => (
                            <MenuItem key={s.sessionId} value={s.sessionId} style={{ fontSize: '13px' }}>
                              {s.activityName} — {sessionLabel(s)}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      <CICODateSelect
                        field={`checkin-${o.volunteerId}`}
                        time={checkInTime[o.volunteerId] ?? ''}
                        onChange={(value) => setCheckInTime((prev) => ({ ...prev, [o.volunteerId]: value }))}
                      />
                    </Box>
                  </Box>
                );
              })
            )}
          </>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '12px 16px' }}>
        <TabletButton variant='text' onClick={onClose} disabled={submitting}>
          Cancel
        </TabletButton>
        <TabletButton
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={eligibleCount === 0 || submitting}
          style={{ color: '#fff' }}
        >
          {submitting ? <CircularProgress size={20} /> : `Check In (${eligibleCount})`}
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
}
